/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/**
 * ConnectMacroRenderer - Renders Connect/native Confluence macros inside Forge UIKit ADF
 *
 * This component handles Connect macros (extensionType: 'com.atlassian.confluence.macro.core')
 * by querying the backend for rendered output and evaluating initialization scripts.
 *
 * NOTE: This code is duplicated from Confluence Connect utilities to avoid circular dependencies.
 * Original source: confluence/next/packages/connect-utils/src/
 * See also: confluence/next/packages/fabric-extension-handlers/src/extensions/connect/
 *
 * TODO(brizvash): Extract shared Connect macro rendering utilities to a platform package to avoid
 * code duplication between Confluence and Forge UI Kit.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';

import { css, jsx } from '@compiled/react';
import type ApolloClient from 'apollo-client';
import gql from 'graphql-tag';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- using compiled css

const hiddenStyles = css({
	display: 'none',
});

// GraphQL query to fetch rendered macro output
const MACRO_BODY_RENDERER_QUERY = gql`
	query ConnectMacroRendererQuery(
		$adf: String!
		$containedRender: Boolean = false
		$contentId: ID
		$mode: ContentRendererMode = RENDERER
	) @allowHigherTimeout {
		macroBodyRenderer(
			adf: $adf
			containedRender: $containedRender
			contentId: $contentId
			mode: $mode
		) {
			value
			representation
			mediaToken {
				token
			}
			webResourceDependencies {
				contexts
				keys
				tags {
					js
					css
				}
			}
		}
	}
`;

// WRM resources required for Connect framework
const CONNECT_WRM_RESOURCES = [
	'wr!confluence.web.resources:querystring',
	'wr!confluence.web.resources:navigator-context',
	'wr!com.atlassian.plugins.atlassian-connect-plugin:confluence-atlassian-connect-resources-v5',
];

// TypeScript interfaces
interface MacroBodyRendererResponse {
	macroBodyRenderer: {
		mediaToken: {
			token: string | null;
		} | null;
		representation: string | null;
		value: string | null;
		webResourceDependencies: {
			contexts: string[];
			keys: string[];
			tags: {
				css: string | null;
				js: string | null;
			} | null;
		} | null;
	} | null;
}

interface ExtensionProps {
	extensionKey: string;
	extensionType: string;
	localId?: string;
	parameters?: {
		[key: string]: unknown;
		macroMetadata?: {
			macroId?: { value: string };
			schemaVersion?: { value: string };
			title?: string;
		};
		macroParams?: Record<string, { value: string }>;
	};
}

export interface ConnectMacroRendererProps {
	client: ApolloClient<any>;
	contentId: string;
	errorComponent?: React.ReactNode;
	extension: ExtensionProps;
	loadingComponent?: React.ReactNode;
}

// Declare global window properties
declare global {
	interface Window {
		__CONNECT_RESOURCES_PRELOADED__?: boolean;
		WRM?: {
			require?: (resources: string[], onSuccess: () => void) => void;
		};
	}
}

/**
 * Load WRM resources using window.WRM.require
 */
const loadWrmResources = (
	resources: string[],
	onSuccess: () => void,
	onError: () => void,
): void => {
	const wrm = window.WRM;
	if (!wrm?.require) {
		// WRM not available - this can happen in non-Confluence contexts
		onError();
		return;
	}

	wrm.require(resources, onSuccess);
};

/**
 * Evaluate Connect inline scripts within a container
 * Connect macros have inline scripts with class 'ap-iframe-body-script' that initialize the iframe
 */
const evalConnectScripts = (container: HTMLElement): void => {
	const connectScripts = Array.from(
		container.querySelectorAll('script.ap-iframe-body-script'),
	) as HTMLScriptElement[];
	const macroScripts = Array.from(
		container.querySelectorAll('script.cc-macro-body-script'),
	) as HTMLScriptElement[];

	const allScripts = [...connectScripts, ...macroScripts];

	allScripts.forEach((script) => {
		// Use indirect eval to execute in global scope (required for AMD modules)
		// eslint-disable-next-line no-eval
		const evalInGlobalScope = eval;
		try {
			evalInGlobalScope(script.innerHTML);
		} catch (e) {
			// Log error but don't throw - allow other scripts to continue
			// eslint-disable-next-line no-console
			console.error('[ConnectMacroRenderer] Error evaluating Connect script:', e);
		}
	});
};

// Serialize extension to ADF format for GraphQL query
const serializeExtensionToAdf = (extension: ExtensionProps): string => {
	const adfNode = {
		type: 'doc',
		version: 1,
		content: [
			{
				type: 'extension',
				attrs: {
					extensionType: extension.extensionType,
					extensionKey: extension.extensionKey,
					parameters: extension.parameters || {},
					localId: extension.localId,
				},
			},
		],
	};
	return JSON.stringify(adfNode);
};

/**
 * Cleanup Connect iframes and scripts within a container.
 * This prevents memory leaks and race conditions when the component unmounts.
 *
 * Note: This cleanup is more thorough than what Confluence currently does.
 * Confluence relies on DOM removal, but in SPA contexts (like Forge macros),
 * explicit cleanup helps prevent issues with stale iframe references.
 *
 * See: confluence/next/packages/content-renderer/src/TinyMCEServerFullRenderer.tsx
 * which removes Connect scripts during SSR to avoid race conditions.
 */
const cleanupConnectResources = (container: HTMLElement | null): void => {
	if (!container) {
		return;
	}

	// Remove Connect iframes to ensure they're properly destroyed
	// This prevents issues where Connect scripts might send events to destroyed iframes
	const iframes = container.querySelectorAll('iframe');
	iframes.forEach((iframe) => {
		// Remove src first to stop any pending loads
		iframe.removeAttribute('src');
		iframe.remove();
	});

	// Remove Connect inline scripts to prevent them from being re-evaluated
	// if the component remounts (similar to TinyMCEServerFullRenderer SSR cleanup)
	container.querySelectorAll('script.ap-iframe-body-script').forEach((el) => el.remove());
	container.querySelectorAll('script.cc-macro-body-script').forEach((el) => el.remove());
};

/**
 * ConnectMacroRenderer Component
 *
 * Renders Connect macros by:
 * 1. Querying the Confluence backend for rendered macro HTML
 * 2. Loading required WRM resources
 * 3. Rendering the HTML
 * 4. Evaluating Connect inline scripts
 * 5. Cleaning up iframes and scripts on unmount
 */
export const ConnectMacroRenderer = ({
	client,
	extension,
	contentId,
	loadingComponent,
	errorComponent,
}: ConnectMacroRendererProps): React.JSX.Element | null => {
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<Error | null>(null);
	const [macroHtml, setMacroHtml] = useState<string | null>(null);
	const [cssHtml, setCssHtml] = useState<string | null>(null);
	const [resourcesLoaded, setResourcesLoaded] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);
	const initTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	// Fetch macro rendered output from GraphQL
	useEffect(() => {
		let isMounted = true;

		const fetchMacroData = async () => {
			try {
				setIsLoading(true);
				setError(null);

				const adf = serializeExtensionToAdf(extension);

				const result = await client.query<MacroBodyRendererResponse>({
					query: MACRO_BODY_RENDERER_QUERY,
					variables: {
						adf,
						contentId,
						mode: 'RENDERER',
						containedRender: false,
					},
					fetchPolicy: 'cache-first',
				});

				// Only update state if component is still mounted
				if (!isMounted) {
					return;
				}

				const macroBody = result.data?.macroBodyRenderer;

				if (macroBody?.value) {
					setMacroHtml(macroBody.value);
					setCssHtml(macroBody.webResourceDependencies?.tags?.css || null);
				} else {
					setError(new Error('No macro content returned from server'));
				}
			} catch (e) {
				if (isMounted) {
					setError(e instanceof Error ? e : new Error('Failed to fetch macro data'));
				}
			} finally {
				if (isMounted) {
					setIsLoading(false);
				}
			}
		};

		void fetchMacroData();

		return () => {
			isMounted = false;
		};
	}, [client, extension, contentId]);

	// Load WRM resources and evaluate scripts after HTML is rendered
	const initializeConnect = useCallback(() => {
		if (!containerRef.current || !macroHtml) {
			return;
		}

		// Check if resources are already preloaded
		if (window.__CONNECT_RESOURCES_PRELOADED__) {
			evalConnectScripts(containerRef.current);
			setResourcesLoaded(true);
			return;
		}

		// Load WRM resources then evaluate scripts
		loadWrmResources(
			CONNECT_WRM_RESOURCES,
			() => {
				if (containerRef.current) {
					evalConnectScripts(containerRef.current);
				}
				setResourcesLoaded(true);
			},
			() => {
				// WRM loading failed - still try to render, Connect may not work
				setResourcesLoaded(true);
			},
		);
	}, [macroHtml]);

	// Initialize Connect after HTML is in DOM
	useEffect(() => {
		if (macroHtml && containerRef.current) {
			// Use setTimeout to ensure DOM is updated
			initTimeoutRef.current = setTimeout(initializeConnect, 0);
		}

		// Cleanup on unmount or when dependencies change
		return () => {
			// Clear pending initialization timeout
			if (initTimeoutRef.current) {
				clearTimeout(initTimeoutRef.current);
				initTimeoutRef.current = null;
			}
		};
	}, [macroHtml, initializeConnect]);

	// Cleanup Connect resources when component unmounts
	useEffect(() => {
		// Copy ref to variable for cleanup function (react-hooks/exhaustive-deps requirement)
		const container = containerRef.current;
		return () => {
			cleanupConnectResources(container);
		};
	}, []);

	// Loading state
	if (isLoading) {
		if (loadingComponent) {
			return <div data-testid="connect-macro-renderer-loading">{loadingComponent}</div>;
		}
		// No loading indicator provided - show nothing while loading
		return null;
	}

	// Error state
	if (error) {
		if (errorComponent) {
			return <div data-testid="connect-macro-renderer-error">{errorComponent}</div>;
		}
		// No error component provided - show nothing on error (error is logged via console.error in fetch)
		return null;
	}

	// No content
	if (!macroHtml) {
		return null;
	}

	return (
		<div data-testid="connect-macro-renderer">
			{/* CSS tags - hidden */}
			{cssHtml && (
				<div
					css={hiddenStyles}
					dangerouslySetInnerHTML={{ __html: cssHtml }}
					data-testid="connect-macro-css"
				/>
			)}
			{/* Macro content */}
			<div
				ref={containerRef}
				data-testid="connect-macro-content"
				data-resources-loaded={resourcesLoaded}
				dangerouslySetInnerHTML={{ __html: macroHtml }}
			/>
		</div>
	);
};
